import { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Check, HelpCircle, Circle } from "lucide-react";
import { QuizQuestion } from "@shared/types";
import QuestionRenderer from "@/components/quiz/QuestionRenderer";
import { useT } from "@/lib/i18n";

interface QuestionCardProps {
  question: QuizQuestion;
  value: any;
  onChange: (value: any) => void;
  onFinished?: (value: any) => void; // Called when input signals it's done
  isAnswered: boolean;
  isChanged?: boolean; // True if value differs from saved
  isPreview?: boolean;
  cardRef?: (el: HTMLDivElement | null) => void;
}

// City ID to display name mapping
const CITY_NAMES: Record<string, string> = {
  usa_nyc: "New York City",
  usa_la: "Los Angeles",
  usa_chicago: "Chicago",
  usa_sf: "San Francisco",
  usa_atlanta: "Atlanta",
  uk_london: "London",
};

// Helper to format answer for display
function formatAnswer(question: QuizQuestion, value: any): string {
  if (value === undefined || value === null || value === "") return "";

  // Handle city type - look up display name
  if (question.type === "city") {
    return CITY_NAMES[value] || value;
  }

  if (question.type === "scale") {
    const labels = question.scaleLabels;
    const emojiRegex = /\p{Emoji}/gu;
    if (value <= 2) return labels?.min?.replace(emojiRegex, "").trim() || `${value}`;
    if (value >= 6) return labels?.max?.replace(emojiRegex, "").trim() || `${value}`;
    return "Moderate";
  }

  if (Array.isArray(value)) {
    return value
      .map((v) => {
        const option = question.options?.find((o) => o.value === v);
        return option?.label || v;
      })
      .join(", ");
  }

  if (question.options) {
    const option = question.options.find((o) => o.value === value);
    return option?.label || value;
  }

  return String(value);
}

export default function QuestionCard({
  question,
  value,
  onChange,
  onFinished,
  isAnswered,
  isChanged = false,
  isPreview = false,
  cardRef: externalCardRef,
}: QuestionCardProps) {
  const { t } = useT();
  // Start expanded if not answered and not preview
  const [isExpanded, setIsExpanded] = useState(!isAnswered && !isPreview);
  const internalCardRef = useRef<HTMLDivElement>(null);
  const wasPreviewRef = useRef(isPreview);

  // Merge internal and external refs
  const setCardRef = useCallback((el: HTMLDivElement | null) => {
    (internalCardRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
    externalCardRef?.(el);
  }, [externalCardRef]);

  // Handle the "finished" signal from the input
  const handleFinished = useCallback((value: any) => {
    setIsExpanded(false);
    onFinished?.(value);
  }, [onFinished]);

  // Sync expanded state when preview status changes
  useEffect(() => {
    // Becoming a preview -> collapse
    if (isPreview && !wasPreviewRef.current) {
      setIsExpanded(false);
    }
    // Was preview, now not -> expand if not answered
    if (!isPreview && wasPreviewRef.current && !isAnswered) {
      setIsExpanded(true);
    }
    wasPreviewRef.current = isPreview;
  }, [isPreview, isAnswered]);

  const formattedAnswer = formatAnswer(question, value);

  // Preview card - show the question and answers blurred
  if (isPreview) {
    return (
      <div
        ref={setCardRef}
        className="mb-4 card overflow-hidden relative select-none pointer-events-none"
      >
        <div className="blur-[6px] opacity-50">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 bg-dark-muted/10">
              <HelpCircle className="w-4 h-4 text-dark-muted" />
            </div>
            <div className="flex-1 min-w-0 overflow-hidden">
              <h2 className="font-bold text-dark-text break-words text-base">
                {question.question}
              </h2>
            </div>
            <div className="flex-shrink-0 mt-1 opacity-0">
              <ChevronDown className="w-5 h-5 text-dark-muted" />
            </div>
          </div>
          <div className="pt-6">
            <QuestionRenderer
              question={question}
              value={undefined}
              onChange={() => {}}
            />
          </div>
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-white/40" />
      </div>
    );
  }

  return (
    <div
      ref={setCardRef}
      className="mb-4 card overflow-hidden"
    >
      {/* Header - always visible, clickable when answered */}
      <button
        onClick={() => isAnswered && setIsExpanded(!isExpanded)}
        disabled={!isAnswered}
        className={`w-full text-left ${isAnswered ? "cursor-pointer group" : "cursor-default"}`}
      >
        <div className="flex items-start gap-3">
          {/* Icon - checkmark when changed, grey dot when answered unchanged, question mark when not */}
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 transition-all duration-500 ease-out ${
              isChanged ? "bg-primary-500/20" : isAnswered ? "bg-gray-500/20" : "bg-white/30"
            }`}
          >
            {isChanged ? (
              <Check className="w-4 h-4 text-primary-500" />
            ) : isAnswered ? (
              <Circle className="w-3 h-3 text-gray-400 fill-gray-400" />
            ) : (
              <HelpCircle className="w-4 h-4 text-primary-500" />
            )}
          </div>

          {/* Question text */}
          <div className="flex-1 min-w-0 overflow-hidden">
            <h2 className="font-bold text-dark-text break-words text-base">
              {question.question}
            </h2>

            {/* Answer preview - only when collapsed and answered */}
            <AnimatePresence>
              {!isExpanded && isAnswered && formattedAnswer && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.4, ease: "easeOut" }}
                  className="text-xs text-dark-muted mt-1 break-words line-clamp-2"
                >
                  {formattedAnswer}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Chevron - always takes up space, visible only when answered */}
          <motion.div
            animate={{ rotate: isExpanded ? 180 : 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            className={`flex-shrink-0 mt-1 transition-opacity duration-500 ${
              isAnswered ? "opacity-100" : "opacity-0"
            }`}
          >
            <ChevronDown className="w-5 h-5 text-dark-muted group-hover:text-primary-500 transition-colors" />
          </motion.div>
        </div>
      </button>

      {/* Expandable content */}
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          >
            <div className="pt-6">
              <QuestionRenderer
                question={question}
                value={value}
                onChange={onChange}
                onFinished={handleFinished}
              />

              {/* Optional indicator */}
              {!question.required && !value && (
                <p className="text-center text-dark-muted text-sm mt-4">
                  {t("quiz.optionalHint")}
                </p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
